import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function Soma350mg() {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [cartCount, setCartCount] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState('100pills');
  const [shippingType, setShippingType] = useState('overseas');

  const packagesOverseas = {
    '100pills': { pills: 100, price: 200 },
    '170pills': { pills: 170, price: 300 },
    '250pills': { pills: 250, price: 400 }
  };

  const packagesUStoUS = {
    '180pills': { pills: 180, price: 300 },
    '360pills': { pills: 360, price: 580 },
    '540pills': { pills: 540, price: 850 }
  };

  const currentPackages = shippingType === 'overseas' ? packagesOverseas : packagesUStoUS;

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          const cart = JSON.parse(savedCart);
          const count = cart.reduce((sum: number, item: any) => sum + item.quantity, 0);
          setCartCount(count);
        }
      } catch (error) {
        console.error('Error reading cart from localStorage:', error);
        setCartCount(0);
      }
    };
    
    updateCartCount();
    
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cart') {
        updateCartCount();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const product = {
    id: 105,
    name: 'Soma 350mg',
    category: 'Pain Relief',
    images: [
      'https://public.readdy.ai/ai/img_res/c4cb0d43d8d822f06dec9294b4697995.jpg'
    ],
    rating: 4.7,
    reviewCount: 328,
    inStock: true,
    prescriptionRequired: false
  };

  const addToCart = () => {
    try {
      const selectedPkg = currentPackages[selectedPackage as keyof typeof currentPackages];
      const savedCart = localStorage.getItem('cart');
      const cart = savedCart ? JSON.parse(savedCart) : [];
      
      const shippingLabel = shippingType === 'overseas' ? 'Overseas' : 'US to US';
      
      cart.push({
        id: `${product.id}-${selectedPackage}-${shippingType}`,
        name: `${product.name} - ${selectedPkg.pills} pills (${shippingLabel})`,
        category: product.category,
        price: selectedPkg.price,
        image: product.images[0],
        quantity: quantity,
        prescriptionRequired: product.prescriptionRequired
      });
      
      localStorage.setItem('cart', JSON.stringify(cart));
      setCartCount(cart.reduce((sum: number, item: any) => sum + item.quantity, 0));
      navigate('/cart');
    } catch (error) {
      console.error('Error adding item to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>MediCare</Link>
            <div className="flex items-center space-x-4">
              <Link to="/cart" className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer relative">
                <i className="ri-shopping-cart-line text-xl"></i>
                <span className="absolute -top-1 -right-1 bg-teal-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{cartCount}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img src={product.images[0]} alt={product.name} className="w-full h-96 object-cover object-top rounded-lg shadow-lg" />
          </div>

          <div>
            <span className="text-sm text-teal-600 font-semibold">{product.category}</span>
            <h1 className="text-3xl font-bold text-gray-900 mt-2">{product.name}</h1>

            <div className="flex items-center mb-6 mt-4">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-yellow-400 text-lg`}></i>
                ))}
              </div>
              <span className="ml-2 text-gray-600">({product.reviewCount} reviews)</span>
            </div>

            {/* Shipping Type Selector */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Shipping Type</label>
              <div className="grid grid-cols-2 gap-3">
                <div
                  onClick={() => {
                    setShippingType('overseas');
                    setSelectedPackage('100pills');
                  }}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    shippingType === 'overseas' ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="text-center">
                    <i className="ri-ship-line text-2xl mb-2 text-teal-600"></i>
                    <p className="font-semibold text-gray-900">Overseas</p>
                    <p className="text-xs text-gray-600 mt-1">International Shipping</p>
                  </div>
                </div>
                <div
                  onClick={() => {
                    setShippingType('usToUs');
                    setSelectedPackage('180pills');
                  }}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    shippingType === 'usToUs' ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  <div className="text-center">
                    <i className="ri-truck-line text-2xl mb-2 text-teal-600"></i>
                    <p className="font-semibold text-gray-900">US to US</p>
                    <p className="text-xs text-gray-600 mt-1">Domestic Shipping</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Package</label>
              <div className="space-y-3">
                {Object.entries(currentPackages).map(([key, pkg]) => (
                  <div
                    key={key}
                    onClick={() => setSelectedPackage(key)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedPackage === key ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-900">{pkg.pills} Pills</p>
                        <p className="text-sm text-gray-600">${(pkg.price / pkg.pills).toFixed(2)} per pill</p>
                      </div>
                      <p className="text-2xl font-bold text-teal-600">${pkg.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
              <div className="flex items-center space-x-4">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer">
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center font-semibold">{quantity}</span>
                  <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer">
                    <i className="ri-add-line"></i>
                  </button>
                </div>
                <button onClick={addToCart} className="flex-1 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-semibold">
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Add to Cart - ${(currentPackages[selectedPackage as keyof typeof currentPackages].price * quantity).toFixed(2)}
                </button>
              </div>
            </div>

            <div className="border-t pt-6">
              <p className="text-sm text-gray-600"><strong>SKU:</strong> MED{product.id.toString().padStart(6, '0')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">&copy; 2024 MediCare. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-teal-400 transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
